// REQUEST SIGN-OFF (docs SIGNOFF-SPEC S2): what is being signed off (a level, an area, or
// a system in an area - the scope picked on the page, or edited here), the elements and
// snapshot it covers, the schedule activities that get the star, the views the email
// carries, who is asked and as which star, and the link's terms (one use, 72 hours, a
// reminder a day before it expires).
//
// The emailed pictures are rendered on the server from the scope's tiles
// (utils/signoffRequests.viewSvg). The browser no longer renders them: the 3D viewer it
// drove is gone from the page (owner 2026-11-05).
import React, { useEffect, useMemo, useState } from "react";
import { Alert, AutoComplete, Button, Col, DatePicker, Input, Modal, Radio, Row, Select, Space, Table, Tag, Typography } from "antd";
import { DeleteOutlined, StarFilled } from "@ant-design/icons";
import moment from "moment";
import addNotification from "helpers/notify";
import { getSignoffContacts, previewSignoffRequest, sendSignoffRequest } from "api/signoff";
import ActivityFilterSelect from "components/Coordination/ActivityFilterSelect";
import { RING } from "./signoffState";

// the three stars (owner 2026-11-01): blue owner/GC, gold contractor / tenant / BidLight
// client, silver another trade partner (listed, colours no tile)
export const GRANT_OPTIONS = [
  { value: "ownerGc", label: <span><StarFilled style={{ color: RING.ownerGc }} /> Blue · owner/GC</span> },
  { value: "tenant", label: <span><StarFilled style={{ color: RING.tenant }} /> Gold · contractor</span> },
  { value: "partner", label: <span><StarFilled style={{ color: RING.partner }} /> Silver · trade partner</span> },
];

const isEmail = (s) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(s || "").trim());
const keyOf = (t) => `${t.kind}|${t.levelKey || t.level || ""}|${t.areaSetId || ""}|${t.system || ""}|${t.versionId || ""}`;

export default function RequestSignoffModal({ projectId, areas, levels, tiles = [], initial, defaultGrant = "ownerGc", onClose, onSent }) {
  const [targets, setTargets] = useState(initial || []);
  const [phase, setPhase] = useState("");
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [known, setKnown] = useState([]);
  const [recipients, setRecipients] = useState([]);
  const [typed, setTyped] = useState("");
  const [rule, setRule] = useState("own");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  // the deadline, and the sign-off date agreed on (today or earlier) it counts from
  const [requiredBy, setRequiredBy] = useState(null);
  const [agreedAt, setAgreedAt] = useState(moment());
  // activities picked for the gold star ("id,id"); empty = the ones the targets imply
  const [activityIds, setActivityIds] = useState("");
  // the project's people, each partner's roster (with roles) and the saved recipients
  useEffect(() => { getSignoffContacts(projectId).then((r) => setKnown(r.contacts || [])).catch(() => setKnown([])); }, [projectId]);

  // what can be picked: every level, every current area, and every system of an area
  const options = useMemo(() => {
    const out = [];
    Object.entries(levels || {}).forEach(([k, name]) => out.push({ value: `level|${k}`, label: `Level · ${name}`, t: { kind: "level", levelKey: k, level: name } }));
    Object.entries(areas || {}).filter(([, a]) => a.current).forEach(([id, a]) => {
      out.push({ value: `area|${id}`, label: [a.name, a.level].filter(Boolean).join(" · "), t: { kind: "area", areaSetId: id } });
      (a.systems || []).forEach((sys) => out.push({ value: `system|${id}|${sys}`, label: `${sys} · ${a.name}${a.level ? ` · ${a.level}` : ""}`, t: { kind: "system", areaSetId: id, system: sys } }));
    });
    return out.sort((a, b) => a.label.localeCompare(b.label, undefined, { numeric: true }));
  }, [areas, levels]);
  const labelOf = (t) => {
    if (t.kind === "level") return t.level || (levels && levels[t.levelKey]) || t.levelKey;
    if (t.kind === "revision") return t.label || "Model revision";
    const a = (areas && areas[t.areaSetId]) || {};
    return [t.system, a.name, a.level].filter(Boolean).join(" · ");
  };

  // the preview follows the targets
  useEffect(() => {
    if (!targets.length) { setPreview(null); return undefined; }
    let live = true;
    setLoading(true);
    setErr("");
    previewSignoffRequest(projectId, targets.map((t) => ({ ...t, level: t.level || (levels && levels[t.levelKey]) || "" })))
      .then((p) => { if (live) setPreview(p); })
      .catch((e) => { if (live) setErr(e.message); })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [projectId, targets, levels]);

  const addRecipient = (value) => {
    const k = known.find((r) => r.email === value);
    const email = String((k && k.email) || value || "").trim().toLowerCase();
    if (!isEmail(email)) { addNotification(`"${value}" is not an email address`, "danger"); return; }
    if (recipients.some((r) => r.email === email)) return;
    setRecipients([...recipients, {
      email, name: (k && k.name) || "", company: (k && k.company) || "", role: (k && k.role) || "",
      grant: (k && k.grant) || defaultGrant, userId: (k && k.userId) || "",
    }]);
    setTyped("");
  };
  const setR = (i, patch) => setRecipients(recipients.map((r, j) => (j === i ? { ...r, ...patch } : r)));

  const send = async () => {
    setSending(true);
    try {
      const out = await sendSignoffRequest(projectId, {
        phase, rule, message, recipients, activityIds, views: [],
        requiredBy: requiredBy ? requiredBy.format("YYYY-MM-DD") : null,
        agreedAt: agreedAt ? agreedAt.format("YYYY-MM-DD") : null,
        targets: targets.map((t) => ({ ...t, level: t.level || (levels && levels[t.levelKey]) || "" })),
      });
      const failed = out.sent.filter((s) => !s.sent);
      addNotification(failed.length ? `Sent to ${out.sent.length - failed.length}; ${failed.length} could not be mailed` : `Sign-off requested from ${out.sent.length} ${out.sent.length === 1 ? "person" : "people"}`, failed.length ? "warning" : "success");
      onSent();
    } catch (e) {
      addNotification(e.message, "danger");
    }
    setSending(false);
  };

  const expires = moment().add(72, "hours");
  // a link lasts three days, or to the end of the required-by day when that is later
  const expiresAt = requiredBy && requiredBy.clone().endOf("day").isAfter(expires) ? requiredBy.clone().endOf("day") : expires;
  const title = `Request sign-off${phase ? ` · ${phase}` : ""}${targets.length ? ` · ${targets.map(labelOf).join(" & ")}` : ""}`;
  return (
    <Modal open title={title} width={1180} onCancel={onClose} footer={null} destroyOnClose className="signoff-request-modal">
      <Row gutter={16}>
        <Col xs={24} lg={15}>
          <div className="signoff-box">
            <div className="signoff-box-title">What is being signed off</div>
            <Space wrap size={6} className="mb-2">
              {targets.map((t) => (
                <Tag key={keyOf(t)} color="blue" closable onClose={() => setTargets(targets.filter((x) => keyOf(x) !== keyOf(t)))}>{labelOf(t)}</Tag>
              ))}
              <Select showSearch placeholder="+ level, area or a system in an area" style={{ minWidth: 240 }} value={null} optionFilterProp="label"
                      options={options.filter((o) => !targets.some((t) => keyOf(t) === keyOf(o.t)))}
                      onChange={(v) => { const o = options.find((x) => x.value === v); if (o) setTargets([...targets, o.t]); }} />
            </Space>
            <Input className="mb-2" placeholder="Phase (e.g. IFF, IFC)" value={phase} maxLength={120} onChange={(e) => setPhase(e.target.value)} />
            {err ? <Alert type="error" showIcon message={err} className="mb-2" /> : null}
            {preview ? (
              <>
                <Row gutter={8} className="mb-2">
                  <Col span={6}><small className="signoff-muted d-block">Tiles</small><b>{preview.tiles.toLocaleString()}</b></Col>
                  <Col span={6}><small className="signoff-muted d-block">Elements</small><b>{preview.elements.toLocaleString()}</b></Col>
                  <Col span={12}><small className="signoff-muted d-block">Snapshot (frozen on send)</small><b>{preview.exportVersion ? `Export ${preview.exportVersion}` : "No model export"} · {moment().format("MMM D, h:mm A")}</b></Col>
                </Row>
                <div className="mb-2" data-testid="request-grid"><small className="signoff-muted">Building grid: </small><b>{preview.gridSpan ? `Grid lines ${preview.gridSpan}` : "no grid lines round these tiles"}</b></div>
                {preview.coverNote ? <Alert type="warning" showIcon message={preview.coverNote} className="mb-2" /> : null}
                <small className="signoff-muted d-block">Schedule activities that get the gold star</small>
                {/* like the Issues Manager: any level of the schedule, with everything below it */}
                <ActivityFilterSelect projectId={projectId} value={activityIds} onChange={(v) => setActivityIds(v || "")} />
                {!activityIds ? (
                  <div className="signoff-acts">
                    <small className="signoff-muted">Automatic from the targets ({preview.activityCount}): </small>
                    {preview.activities.length ? preview.activities.slice(0, 12).map((a) => a.name).join(" · ") : <span className="signoff-muted">none tied to these targets - a contractor sign-off is still recorded on the timeline.</span>}
                    {preview.activities.length > 12 ? ` +${preview.activities.length - 12} more` : ""}
                  </div>
                ) : null}
              </>
            ) : <Typography.Text type="secondary">{loading ? "Working out what this covers…" : "Pick at least one level, area or system."}</Typography.Text>}
          </div>
          {preview && preview.views && preview.views.length ? (
            <div className="signoff-box">
              <div className="signoff-box-title">What the email shows <small className="signoff-muted">the scope drawn from four angles</small></div>
              <div className="signoff-views">
                {preview.views.map((v) => (
                  // eslint-disable-next-line react/no-danger
                  <figure key={v.kind}><div dangerouslySetInnerHTML={{ __html: v.svg }} /><figcaption>{v.title}</figcaption></figure>
                ))}
              </div>
            </div>
          ) : null}
        </Col>
        <Col xs={24} lg={9}>
          <div className="signoff-box">
            <div className="signoff-box-title">Recipients</div>
            <Table size="small" rowKey="email" pagination={false} dataSource={recipients} locale={{ emptyText: "Nobody yet" }}
                   columns={[
                     { title: "Person", render: (_, r, i) => (
                       <div>
                         <Input size="small" placeholder="Name" value={r.name} onChange={(e) => setR(i, { name: e.target.value })} />
                         <Input size="small" className="mt-1" placeholder="Company" value={r.company} onChange={(e) => setR(i, { company: e.target.value })} />
                         <Input size="small" className="mt-1" placeholder="Role (e.g. Owner rep, PM, Foreman)" value={r.role} maxLength={80} onChange={(e) => setR(i, { role: e.target.value })} />
                         <small className="signoff-muted">{r.email}</small>
                       </div>
                     ) },
                     { title: "Signs as", width: 170, render: (_, r, i) => (
                       <Select size="small" value={r.grant} onChange={(v) => setR(i, { grant: v })} style={{ width: 160 }} options={GRANT_OPTIONS} />
                     ) },
                     { title: "", width: 36, render: (_, r) => <Button size="small" type="text" icon={<DeleteOutlined />} onClick={() => setRecipients(recipients.filter((x) => x.email !== r.email))} /> },
                   ]} />
            <AutoComplete className="mt-2" style={{ width: "100%" }} value={typed} onChange={setTyped} onSelect={addRecipient}
                          options={known.filter((k) => !recipients.some((r) => r.email === k.email)).map((k) => ({ value: k.email, label: [k.name, k.company, k.role, k.email].filter(Boolean).join(" · ") }))}
                          filterOption={(input, o) => String(o.label).toLowerCase().includes(input.toLowerCase())}>
              <Input placeholder="Add from the team and partners, or type a new contact's email" onPressEnter={() => typed && addRecipient(typed)} />
            </AutoComplete>
            <Radio.Group className="mt-2 d-flex flex-column" value={rule} onChange={(e) => setRule(e.target.value)}>
              <Radio value="own">Any one owner/GC recipient completes the blue star (the others' links close)</Radio>
              <Radio value="allOwnerGc">Blue needs all owner/GC recipients</Radio>
            </Radio.Group>
          </div>
          <div className="signoff-box">
            <div className="signoff-box-title">Dates</div>
            <div className="tile-row"><span>Sign-off required by</span>
              <DatePicker size="small" value={requiredBy} onChange={setRequiredBy} disabledDate={(d) => d && d.isBefore(moment().startOf("day"))} placeholder="No deadline" />
            </div>
            <div className="tile-row"><span>Agreed sign-off date</span>
              <DatePicker size="small" value={agreedAt} onChange={setAgreedAt} allowClear={false} disabledDate={(d) => d && d.isAfter(moment().endOf("day"))} />
            </div>
            <small className="signoff-muted d-block">The sign-off counts from the agreed date on the timeline (today or earlier); when each person actually signs is recorded too.</small>
          </div>
          <div className="signoff-box">
            <div className="signoff-box-title">Link</div>
            <div className="tile-row"><span>Expires</span><b>{expiresAt.format("ddd, MMM D, h:mm A")}{expiresAt.isAfter(expires) ? " · the required-by day" : " · 3 days"}</b></div>
            <div className="tile-row"><span>Use</span><span>Once, by the named person only</span></div>
            <div className="tile-row"><span>Reminder</span><span>24 h before expiry</span></div>
            <small className="signoff-muted d-block mb-2">Over a recipient's weekend the mail goes on their Monday morning, and the three days start then.</small>
            <Input.TextArea rows={3} placeholder="A short message to the recipients" value={message} maxLength={2000} onChange={(e) => setMessage(e.target.value)} />
            <div className="d-flex justify-content-end mt-2" style={{ gap: 8 }}>
              <Button onClick={onClose}>Cancel</Button>
              <Button type="primary" loading={sending} disabled={!targets.length || !recipients.length || !!err || loading || !preview} onClick={send} data-testid="signoff-send">
                Send to {recipients.length} {recipients.length === 1 ? "person" : "people"}
              </Button>
            </div>
          </div>
        </Col>
      </Row>
    </Modal>
  );
}
